import { useTranslation } from '../i18n/useTranslation';
import type { PanelDataState } from '../types/gateway';

interface PanelStateProps {
  /** Resolved tri-state (+ready) from usePanelState. */
  state: PanelDataState;
  /** Stable key → drives data-testid `panel-{key}-{loading|empty|degraded}`. */
  panelKey: string;
  /** Optional override for the live-empty message (else generic panel.empty). */
  emptyMessage?: string;
}

/**
 * DDD #180 S1.2 — shared per-panel data-state display.
 *
 * Renders nothing for 'ready' (caller renders the data). For the other states
 * it shows a consistent, accessible indicator so beta users distinguish
 * "no data yet" (live-empty, informative) from "this is broken" (degraded).
 * Replaces ad-hoc binary `!data && <AwaitingX/>` blocks.
 */
export function PanelState({ state, panelKey, emptyMessage }: PanelStateProps) {
  const { t } = useTranslation();

  if (state === 'ready') return null;

  if (state === 'loading') {
    return (
      <div
        role="status"
        aria-busy="true"
        data-testid={`panel-${panelKey}-loading`}
        className="flex items-center justify-center gap-2 h-full min-h-[3rem] text-[10px] font-mono uppercase tracking-widest text-muted-foreground"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-pulse" aria-hidden="true" />
        {t('panel.loading')}
      </div>
    );
  }

  if (state === 'degraded') {
    return (
      <div
        role="alert"
        data-testid={`panel-${panelKey}-degraded`}
        className="flex items-center justify-center gap-2 h-full min-h-[3rem] px-3 text-[10px] font-mono uppercase tracking-wide text-red-400 border border-red-800/50 bg-red-950/30 rounded-sm"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
        {t('panel.degraded')}
      </div>
    );
  }

  // live-empty
  return (
    <div
      data-testid={`panel-${panelKey}-empty`}
      className="flex items-center justify-center h-full min-h-[3rem] px-3 text-[10px] font-mono text-muted-foreground/80 text-center"
    >
      {emptyMessage ?? t('panel.empty')}
    </div>
  );
}
